import { useEffect } from 'react';
import { DataService } from '../services/data-service';
import { emailListAddresses } from '../services/email-recipient-memory';

/** One address of a sent quote email, with where it came from and its Clients person. */
export interface SentRecipient {
    field: 'to' | 'cc';
    email: string;
    source: string | null;
    contactId: string | null;
}

/**
 * Every address of a sent quote email (2026-10-13): the modal's
 * recipient_sources when it sent them, else the To and Cc lists split, so a
 * To of several people is never read as one address.
 */
export function sentQuoteRecipients(detail: Record<string, any>): SentRecipient[] {
    if (Array.isArray(detail?.recipient_sources) && detail.recipient_sources.length) {
        return detail.recipient_sources
            .filter((entry: any) => entry && typeof entry.email === 'string' && entry.email.includes('@'))
            .map((entry: any) => ({
                field: entry.field === 'cc' ? 'cc' : 'to',
                email: String(entry.email).trim().toLowerCase(),
                source: typeof entry.source === 'string' && entry.source ? entry.source : null,
                contactId: typeof entry.contactId === 'string' && entry.contactId ? entry.contactId : null,
            }));
    }
    return [
        ...emailListAddresses(detail?.recipient).map(email => ({ field: 'to' as const, email, source: null, contactId: null })),
        ...emailListAddresses(detail?.cc).map(email => ({ field: 'cc' as const, email, source: null, contactId: null })),
    ];
}

/** The activity line: each address with where it came from. */
export function describeSentQuoteRecipients(recipients: SentRecipient[]): string {
    const part = (entry: SentRecipient) => (entry.source ? `${entry.email} (${entry.source})` : entry.email);
    const to = recipients.filter(entry => entry.field === 'to').map(part);
    const cc = recipients.filter(entry => entry.field === 'cc').map(part);
    if (!to.length && !cc.length) return 'Quote emailed to recipient';
    return `Quote emailed to ${to.length ? to.join(', ') : 'no To address'}${cc.length ? `; cc ${cc.join(', ')}` : ''}`;
}

export interface UseAppEventListenersOptions {
    setToast: (toast: { message: string; type: 'warning' | 'success' | 'error' | 'info' }) => void;
    currentProjectIdRef?: React.MutableRefObject<string | null | undefined>;
    projectsRef: React.MutableRefObject<Array<any>>;
}

/**
 * Registers window event listeners for cross-subsystem telemetry and notifications:
 * - qm-cost-zero: warn of pricing anomaly
 * - qm-prompt-save-failed / backend-sync / idb-sync: prompt storage status toasts
 * - qm-quote-email-sent: log email activity to CRM with best-effort contact resolution
 */
export function useAppEventListeners({
    setToast,
    currentProjectIdRef,
    projectsRef,
}: UseAppEventListenersOptions): void {
    useEffect(() => {
        const onCostZero = (e: Event) => {
            const d = (e as CustomEvent).detail || {};
            console.warn('[QM] cost-zero anomaly:', d.pipelineId, d.stageName, d.model, '- check provider response');
        };
        const onPromptSaveFailed = (e: Event) => {
            const d = (e as CustomEvent)?.detail || {};
            if (d.reason === 'quota_exceeded') {
                setToast({ message: 'Browser storage is full. Your prompt edit was not saved. Export a backup and clear old history.', type: 'error' });
            } else if (d.reason === 'quota_exceeded_partial_save') {
                setToast({ message: 'Prompt saved after pruning older history. Consider exporting a backup.', type: 'warning' });
            } else {
                setToast({ message: `Prompt save issue (${d.reason || 'unknown'}). See Developer > Prompts.`, type: 'error' });
            }
        };
        const onBackendSync = () => {
            setToast({ message: 'Prompt sync to backend is degraded. Edits saved locally; cloud sync will resume when the backend is reachable.', type: 'warning' });
        };
        const onIdbSync = () => {
            setToast({ message: 'Prompt persistence at risk: both IndexedDB and backend failed. Export a backup now - saves may be lost on reload.', type: 'error' });
        };
        const onQuoteEmailSent = async (e: Event) => {
            const d = (e as CustomEvent).detail || {};
            try {
                const pid = d.quote_id || currentProjectIdRef?.current;
                if (pid) {
                    const proj = projectsRef.current.find((p: any) => p.id === pid);
                    // camelCase online (apiFetch canon), snake_case in the
                    // offline Dexie summary.
                    const companyId = proj?.companyId || proj?.company_id || null;
                    // Each address is attributed (2026-10-13): the modal says
                    // where it came from and which Clients person it is; any
                    // address it could not place is looked up among the
                    // linked firm's contacts by exact email. The activity's
                    // person is the first requester in To. Non-blocking: a
                    // lookup miss just leaves contact_id null.
                    const recipients = sentQuoteRecipients(d);
                    if (companyId && recipients.some(entry => !entry.contactId)) {
                        try {
                            const contacts = await DataService.crmContacts.getByCompany(companyId);
                            for (const entry of recipients) {
                                if (entry.contactId) continue;
                                const match = (contacts || []).find((c: any) => (c.email || '').trim().toLowerCase() === entry.email);
                                entry.contactId = match?.id || null;
                            }
                        } catch { /* non-blocking */ }
                    }
                    const person = recipients.find(entry => entry.field === 'to' && entry.contactId)
                        || recipients.find(entry => entry.contactId);
                    const contactId: string | null = person?.contactId || null;
                    DataService.crmActivities.log({
                        company_id: companyId,
                        contact_id: contactId,
                        project_id: pid,
                        activity_type: 'email_sent',
                        description: describeSentQuoteRecipients(recipients),
                        metadata: {
                            recipient: d.recipient,
                            cc: d.cc,
                            recipients,
                            subject: d.subject,
                            pdf_filename: d.pdf_filename,
                            pdf_hash: d.pdf_hash,
                            mode: d.mode,
                            timestamp: d.timestamp,
                        },
                    }).catch((err: any) => console.warn('[QM] CRM email_sent log failed:', err?.message));
                }
            } catch (err: any) {
                console.warn('[QM] qm-quote-email-sent handler failed:', err?.message);
            }
        };

        window.addEventListener('qm-cost-zero', onCostZero);
        window.addEventListener('qm-prompt-save-failed', onPromptSaveFailed);
        window.addEventListener('qm-prompt-backend-sync-failed', onBackendSync);
        window.addEventListener('qm-prompt-idb-sync-failed', onIdbSync);
        window.addEventListener('qm-quote-email-sent', onQuoteEmailSent);

        return () => {
            window.removeEventListener('qm-cost-zero', onCostZero);
            window.removeEventListener('qm-prompt-save-failed', onPromptSaveFailed);
            window.removeEventListener('qm-prompt-backend-sync-failed', onBackendSync);
            window.removeEventListener('qm-prompt-idb-sync-failed', onIdbSync);
            window.removeEventListener('qm-quote-email-sent', onQuoteEmailSent);
        };
    }, [setToast, currentProjectIdRef, projectsRef]);
}
